import {addSortToQuery, getSortFromQuery} from '@github-ui/dashboard-sorting-dropdown/query'
import {hpcAttrs} from '@github-ui/hpc'
import {useRouteQuery, useSuspenseRouteQuery} from '@github-ui/react-core/future/use-route-query'
import type {Location} from '@github-ui/react-router'
import {Link, useLocation, useNavigation} from '@github-ui/react-router'
import {
  ReposList,
  ReposListLoadingSkeleton,
  ReposListProTip,
  sortingOptionsWithRelevance,
  useCompactModeSetting,
} from '@github-ui/repos-list'
import {NoReposBlankslate} from '@github-ui/repos-list-blankslate'
import {Pagination} from '@primer/react'
import {useRef} from 'react'

import {useFinderScreenReaderAnnouncements} from '../hooks/use-finder-screen-reader-announcements'
import {useReposFinderState} from '../hooks/use-repos-finder-state'
import {reposFinderLayoutRoute} from './repos-finder-layout-route'
import {reposFinderPageRoute} from './repos-finder-page-route'

const DEFAULT_SORTING_KEY = 'relevance'
function isSameLocation(a: Location, b: Location): boolean {
  return a.pathname === b.pathname && a.search === b.search
}

export function ReposFinderPage() {
  const navigation = useNavigation()
  const location = useLocation()

  // We compare navigation.location (the pending destination) with the current location
  // to determine if we're navigating to a *different* page. Using navigation.state === 'loading'
  // alone isn't sufficient because it also returns true when re-clicking the same sidebar item
  // triggers a background refetch, which we don't want to show as a full page loading state.
  //
  // We also can't rely on `isLoading` from `useSuspenseRouteQuery` because the route query is
  // blocking - the component suspends until data is fetched, so by the time we have access to
  // the query result, the data is already loaded. This doesn't give us a chance to show the
  // skeleton during navigation. Instead, we detect navigation at this level before suspense kicks in.
  const isNavigating = Boolean(navigation.location && !isSameLocation(navigation.location, location))

  if (isNavigating) {
    return <ReposListLoadingSkeleton />
  }

  return <ReposFinderPageContent />
}

export function ReposFinderPageContent() {
  const {data: payload} = useSuspenseRouteQuery(reposFinderPageRoute, 'mainQuery')
  const {
    data: {compactMode},
  } = useRouteQuery(reposFinderLayoutRoute, 'mainQuery')

  const {fetchedQuery, results, currentPage, fetchResults} = useReposFinderState(payload)
  const hasQuery = Boolean(fetchedQuery && fetchedQuery.trim().length > 0)

  const {updateCompactModeSetting} = useCompactModeSetting(compactMode, 'repos_finder_compact_mode')

  const {emptyMessages} = useFinderScreenReaderAnnouncements(
    false,
    currentPage,
    fetchedQuery,
    results,
    DEFAULT_SORTING_KEY,
    hasQuery ? payload.errorMessage : undefined,
  )

  const reposListRef = useRef<HTMLUListElement>(undefined)

  return (
    <>
      <div className="tmp-my-3">
        <div {...hpcAttrs()}>
          <ReposList
            listRef={reposListRef}
            query={fetchedQuery}
            showNameWithOwner
            compactMode={compactMode}
            onCompactModeChanged={updateCompactModeSetting}
            repositoryCount={results.repositoryCount}
            repos={results.repositories}
            onSortingItemSelect={sort => fetchResults({q: addSortToQuery(fetchedQuery, sort)})}
            sortingItemSelected={getSortFromQuery(fetchedQuery)}
            defaultSortingKey={DEFAULT_SORTING_KEY}
            sortingOptions={sortingOptionsWithRelevance}
            directionRestrictedKeys={['relevance']}
            noReposMessage={<NoReposBlankslate message={emptyMessages} />}
            linkAs={Link}
          />
        </div>
      </div>
      {results.pageCount > 1 && (
        <Pagination
          pageCount={results.pageCount}
          currentPage={currentPage}
          onPageChange={(e, page) => {
            // Avoid adding hash parameters
            e.preventDefault()

            reposListRef.current?.focus()
            window.scrollTo({top: 0})
            fetchResults({q: fetchedQuery, page})
          }}
        />
      )}
      <ReposListProTip savedViewsAvailable />
    </>
  )
}
